import { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, Calendar, Clock, Pencil, Trash2 } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import type { DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import type { AvailabilityRequest } from '../types';
import { useCreateRequest } from '../hooks/useApi';
import { trackCreateRequest } from '../lib/posthog';

function toIso(date: Date | undefined): string {
  if (!date) return '';
  return date.toISOString().slice(0, 10);
}

function fromIso(iso: string): Date | undefined {
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function formatDisplay(iso: string): string {
  if (!iso) return '';
  const d = fromIso(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

interface RequestFormProps {
  facilityId: string;
  campsiteId?: string;
  initialStartDate?: string;
  initialEndDate?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function RequestForm({ facilityId, campsiteId, initialStartDate, initialEndDate, onSuccess, onCancel }: RequestFormProps) {
  void campsiteId;
  const [startDate, setStartDate] = useState(initialStartDate ?? '');
  const [endDate, setEndDate] = useState(initialEndDate ?? '');
  const [stayLength, setStayLength] = useState(2);
  const [arrivalDays, setArrivalDays] = useState<number[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const today = useMemo(() => new Date(), []);

  const createRequest = useCreateRequest();

  const dateRange: DateRange = {
    from: fromIso(startDate),
    to: fromIso(endDate),
  };

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setStartDate(toIso(range?.from));
    setEndDate(toIso(range?.to));
    if (range?.from && range?.to && range.from.getTime() !== range.to.getTime()) {
      setIsCalendarOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setIsCalendarOpen(false);
      }
    };
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCalendarOpen]);

  const toggleArrivalDay = (day: number) => {
    setArrivalDays((current) =>
      current.includes(day)
        ? current.filter((v) => v !== day)
        : [...current, day].sort((a, b) => a - b),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) >= new Date(endDate)) {
      setError('End date must be after start date');
      return;
    }

    try {
      await createRequest.mutateAsync({
        facilityId,
        startDate,
        endDate,
        stayLength,
        arrivalDays,
        isActive: true,
      });
      trackCreateRequest(facilityId);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create request');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-emerald-100 rounded-lg">
          <Bell className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">Create Availability Alert</h3>
          <p className="text-sm text-gray-500">Get notified when a spot opens up</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div className="relative" ref={calendarRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Dates</label>
          <button
            type="button"
            onClick={() => setIsCalendarOpen((o) => !o)}
            className="w-full flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-left focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
          >
            <Calendar className="w-5 h-5 text-gray-400 shrink-0" />
            <span className={startDate ? 'text-gray-900' : 'text-gray-400'}>
              {startDate && endDate
                ? `${formatDisplay(startDate)} \u2013 ${formatDisplay(endDate)}`
                : startDate
                  ? `${formatDisplay(startDate)} \u2013 Select end`
                  : 'Select dates'}
            </span>
          </button>

          {isCalendarOpen && (
            <div className="absolute left-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-30 p-4 w-max max-w-[calc(100vw-2rem)]">
              <DayPicker
                mode="range"
                selected={dateRange}
                onSelect={handleDateRangeSelect}
                numberOfMonths={2}
                classNames={{ months: 'flex flex-col sm:flex-row gap-4' }}
                disabled={{ before: today }}
                defaultMonth={dateRange.from ?? today}
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Length of stay (nights)
          </label>
          <input
            type="number"
            min={1}
            max={30}
            value={stayLength}
            onChange={(e) => {
              const parsed = Number.parseInt(e.target.value, 10);
              setStayLength(Number.isFinite(parsed) ? parsed : 1);
            }}
            className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Arrive on specific days (optional)
          </label>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAY_OPTIONS.map((day) => {
              const active = arrivalDays.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleArrivalDay(day.value)}
                  className={`px-2.5 py-1.5 rounded-lg border text-xs transition-colors ${
                    active
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createRequest.isPending}
            className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {createRequest.isPending ? (
              <>
                <Clock className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Bell className="w-4 h-4" />
                Create Alert
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}

interface RequestListItemProps {
  request: AvailabilityRequest;
  onCancel: (id: number) => void;
  onEdit: (request: AvailabilityRequest) => void;
  isCancelling?: boolean;
}

export function RequestListItem({ request, onCancel, onEdit, isCancelling }: RequestListItemProps) {
  const statusLabel = request.isActive ? 'Active' : 'Inactive';
  const statusColor = request.isActive
    ? 'bg-emerald-100 text-emerald-800'
    : 'bg-gray-100 text-gray-800';

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-1">
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColor}`}>
            {statusLabel}
          </span>
          <span className="text-sm text-gray-500">
            Created {new Date(request.createdAt).toLocaleDateString()}
          </span>
        </div>
        {request.facilityName && (
          <p className="text-sm font-medium text-gray-700 mb-0.5">{request.facilityName}</p>
        )}
        <p className="font-medium text-gray-900">
          {new Date(request.startDate).toLocaleDateString()} - {new Date(request.endDate).toLocaleDateString()}
        </p>
      </div>
      
      <div className="flex items-center gap-1 ml-4">
        <button
          onClick={() => onEdit(request)}
          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <Pencil className="w-4 h-4" />
        </button>
        {request.isActive && (
          <button
            onClick={() => onCancel(request.id)}
            disabled={isCancelling}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}
